import type { AttendanceStatus } from './scheduling';

/** 'YYYY-MM' over the UTC calendar month of a session's `startAt`. */
export type MonthKey = string;

/**
 * The name a parent is shown: the nickname the family actually uses, or the
 * last word of the full name the way Vietnamese addresses a person.
 * Pure — no DB column, no API field of its own beyond what a payload copies.
 */
export function progressDisplayName(name: string, nickname?: string | null): string {
  const nick = nickname?.trim();
  if (nick) return nick;
  return name.trim().split(/\s+/).filter(Boolean).at(-1) ?? name.trim();
}

/**
 * The month before `month`, as string math on 'YYYY-MM'.
 * Deliberately not `new Date(month)` + `setMonth`: a bare 'YYYY-MM' parses as
 * UTC in some engines and local in others, and this value keys a database
 * lookup on both sides of a network boundary.
 */
export function prevMonthKey(month: MonthKey): MonthKey {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1, 1));
  d.setUTCMonth(d.getUTCMonth() - 1);
  return monthKeyOfUtc(d);
}

/**
 * The month after `month`, as string math on 'YYYY-MM' — the forward twin of
 * `prevMonthKey` above, for the same reason: a bare 'YYYY-MM' parses
 * inconsistently across engines, so every month-arithmetic caller goes
 * through one of these two functions rather than `new Date(month)`.
 */
export function nextMonthKey(month: MonthKey): MonthKey {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1, 1));
  d.setUTCMonth(d.getUTCMonth() + 1);
  return monthKeyOfUtc(d);
}

/**
 * The UTC calendar month of a date-only / UTC-naive wall-clock value —
 * `ClassSession.startAt`, and nothing else. INVARIANTS §2: vnTime's +07:00
 * windows are for REAL timestamps and shift these by a day.
 */
export function monthKeyOfUtc(d: Date): MonthKey {
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/**
 * A snapshot fragment shown on its own line (the label grids of E3 and the
 * Tiến độ tab): trimmed, with a closing "." added unless it already ends in
 * `.?!`. Case is left exactly as written; only `sentence()` lowercases, and
 * only for label-prefixed lines ("Tên mục: nội dung").
 */
export function fullStop(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '';
  return /[.?!]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/**
 * Renders a snapshot of `NoteItem.content` (or any short user-written
 * fragment) as one sentence for a label-prefixed line: "Tên mục: {sentence}".
 * Shared by `apps/web`'s `SessionCard` and the dashboard's read surfaces
 * (port guide `20-port-guide-templates.md` §4) so the two never drift.
 *
 * Trims, then lowercases the opening letter ONLY when the second word also
 * starts with a lowercase letter — the port guide's deliberately simplified
 * stand-in for "the opening word is not a known proper noun" (a real
 * proper-noun lookup is out of scope): a normal Vietnamese fragment that got
 * capitalized only because it opens a sentence has a lowercase word right
 * after it ("Chưa thuộc bài" → "chưa thuộc bài."), while a multi-word proper
 * noun keeps capitalizing past the first word ("Sonata Ánh Trăng" stays as
 * written). A single-word fragment is left alone (nothing to compare
 * against). Finally appends "." unless the fragment already ends in one of
 * `.?!`. Blank input (after trim) returns ''.
 */
export function sentence(s: string): string {
  const trimmed = s.trim();
  if (!trimmed) return '';

  const words = trimmed.split(/\s+/);
  let result = trimmed;
  if (words.length > 1) {
    const secondFirstChar = words[1]!.charAt(0);
    const looksLowercase = secondFirstChar.toLowerCase() === secondFirstChar && secondFirstChar.toUpperCase() !== secondFirstChar;
    if (looksLowercase) {
      result = trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
    }
  }

  return /[.?!]$/.test(result) ? result : `${result}.`;
}

// ── Note templates (THI-193, sườn nhận xét) ──────────────────────────────

export type NoteTemplatePresetKey = 'music' | 'art' | 'exam' | 'custom';

export interface NoteTemplateItem {
  /** nanoid(8), assigned by the server when missing. */
  id: string;
  name: string;
  hint: string | null;
}

/** `Subject.noteTemplate`. `null` on the subject = free-text form. An empty
 *  group hides in the form. */
export interface NoteTemplate {
  presetKey: NoteTemplatePresetKey | null;
  classItems: NoteTemplateItem[];
  studentItems: NoteTemplateItem[];
  homeworkItems: NoteTemplateItem[];
}

/** One sectioned entry inside a note. `name` is the template item's name
 *  SNAPSHOTTED when the note was written — renaming or deleting the item in
 *  the template never changes what a saved note says. */
export interface NoteItem {
  itemId: string;
  name: string;
  content: string;
}

// ── Session rating (THI-346, mức buổi học) ────────────────────────────────

/** 1 = Báo động … 5 = Xuất sắc. Fixed platform-wide: the parent page, the
 *  sample page, the AI draft and the user guide all speak one scale. */
export type SessionRating = 1 | 2 | 3 | 4 | 5;

/** Best first, the order every distribution line renders in. Emoji and
 *  labels were chosen over stars on 2026-09-28 (a one-star for a child reads
 *  as an insult; a face with a học bạ word is a message, not a score). */
export const SESSION_RATING_LEVELS: readonly { level: SessionRating; emoji: string; label: string }[] = [
  { level: 5, emoji: '🤩', label: 'Xuất sắc' },
  { level: 4, emoji: '🥰', label: 'Tốt' },
  { level: 3, emoji: '🙂', label: 'Khá' },
  { level: 2, emoji: '😤', label: 'Cần chú ý' },
  { level: 1, emoji: '🥵', label: 'Báo động' },
];

export function sessionRatingMeta(level: SessionRating): { level: SessionRating; emoji: string; label: string } {
  return SESSION_RATING_LEVELS.find((l) => l.level === level)!;
}

/** Stored integer → the level, or null for anything outside 1..5. */
export function readSessionRating(v: unknown): SessionRating | null {
  return v === 1 || v === 2 || v === 3 || v === 4 || v === 5 ? v : null;
}

/** `{ level, count }` per level with at least one session, best first. */
export function sessionRatingDistribution(levels: (SessionRating | null | undefined)[]): { level: SessionRating; count: number }[] {
  const counts = new Map<SessionRating, number>();
  for (const l of levels) if (l) counts.set(l, (counts.get(l) ?? 0) + 1);
  return SESSION_RATING_LEVELS.filter((l) => counts.has(l.level)).map((l) => ({ level: l.level, count: counts.get(l.level)! }));
}

// ── Session notes ────────────────────────────────────────────────────────

// ── Homework photos (THI-196, spec §14) ──────────────────────────────────

/** Photos per homework group and per note. Two hard caps bound storage
 *  instead of a plan quota: the count per group and the file size BEFORE
 *  client-side compression (the client resizes to a 2000px long edge). */
export const HOMEWORK_PHOTOS_MAX = 10;
export const HOMEWORK_PHOTO_MAX_BYTES = 10 * 1024 * 1024;

/** The stored shape (`SessionNote.homeworkPhotos` / `StudentSessionNote.homeworkPhotos`).
 *  `key` = `homework/<orgId>/<sessionId>/<id>.jpg` in the PRIVATE bucket: the
 *  prefix is the ownership proof the write path checks. Never a public URL. */
export interface HomeworkPhoto {
  id: string;
  key: string;
  width: number;
  height: number;
  bytes: number;
  createdAt: string;
}

/** What every AUTHENTICATED read surface returns: the stored photo plus a
 *  presigned GET valid for one hour, signed per request. */
export interface HomeworkPhotoRead extends HomeworkPhoto {
  url: string;
}

export type PublicHomeworkPhotoScope = 'CLASS' | 'PERSONAL';

/** The parent page's photo: an allowlist, deliberately without `key`,
 *  `bytes` or `createdAt`. Class photos come before the child's own. */
export interface PublicHomeworkPhoto {
  id: string;
  url: string;
  width: number;
  height: number;
  scope: PublicHomeworkPhotoScope;
}

export interface SessionNoteTier {
  /** Free text, or the "Ghi chú ngoài sườn" when the subject has a template. */
  comment: string | null;
  homework: string | null;
  /** Sectioned content; empty when the note predates the template or the
   *  subject has none. Only items with content are ever stored. */
  items: NoteItem[];
  homeworkItems: NoteItem[];
  /** THI-196: photos on this tier's homework group, empty when none. */
  homeworkPhotos: HomeworkPhotoRead[];
}

export interface StudentSessionNoteRow extends SessionNoteTier {
  studentId: string;
  /** THI-346: the teacher's level for this student's session, or null. */
  rating: SessionRating | null;
}

/** GET /api/sessions/:id/notes — one call feeds the whole form. */
export interface SessionNotesPayload {
  classNote: SessionNoteTier | null;
  studentNotes: StudentSessionNoteRow[];
  /** Students with a personal row — drives the per-row indicator on the
   *  attendance card without the caller having to scan `studentNotes`. */
  notedStudentIds: string[];
  /** The subject's template so the form renders sections without a second
   *  request; `null` = free-text form. */
  template: NoteTemplate | null;
  /** THI-346: `Subject.sessionRatingEnabled`; the form shows the picker only
   *  when true. */
  ratingEnabled: boolean;
}

// ── Timeline ─────────────────────────────────────────────────────────────

export interface ProgressMonthAggregates {
  sessions: number;
  present: number;
  excused: number;
  absent: number;
  notes: number;
}

export type ProgressTimelineItem =
  | {
      kind: 'REVIEW';
      month: MonthKey;
      subjectId: string;
      subjectName: string;
      content: string;
      /** Internal only — never rendered on a parent-facing surface. */
      aiDrafted: boolean;
      /** THI-348: the teacher timeline shows drafts too, marked as such. */
      status: MonthlyReviewStatus;
      teacherName: string | null;
      updatedAt: string;
    }
  | {
      kind: 'SESSION';
      sessionId: string;
      /** ISO instant of `ClassSession.startAt` (UTC-naive wall clock). */
      startAt: string;
      subjectId: string;
      subjectName: string;
      attendance: AttendanceStatus;
      /** THI-346: null when unrated OR when the subject's switch is off. */
      myRating: SessionRating | null;
      myComment: string | null;
      myHomework: string | null;
      /** Sectioned content (THI-193); empty arrays for free-text notes. Same
       *  asymmetry as the class text: class items only for attended sessions. */
      myItems: NoteItem[];
      myHomeworkItems: NoteItem[];
      classItems: NoteItem[];
      classHomeworkItems: NoteItem[];
      /** THI-196: class homework photos (when the student attended) followed
       *  by the student's own, each with a 1-hour presigned GET. */
      homeworkPhotos: HomeworkPhotoRead[];
      /** Present only for sessions the student attended (PRESENT or EXCUSED).
       *  Deliberately asymmetric with the parent page, which shows the class
       *  note for EVERY session in the month including missed ones — see the
       *  spec §2 note. Do not "sync" the two. */
      classComment: string | null;
      classHomework: string | null;
    };

export interface ProgressTimelineMonth {
  month: MonthKey;
  aggregates: ProgressMonthAggregates;
  /** Reviews first, then noted sessions newest-first. */
  items: ProgressTimelineItem[];
}

/**
 * One (month, subject) the student was actually taught in — at least one
 * session that month whose attendance is no longer PENDING — with whether that
 * month's review has been written.
 *
 * Deliberately NOT derived from `months` above: that list is a paginated
 * window (3, +3, API cap 24) meant for reading history, while this one is the
 * nudge list, and a nudge that only covers the loaded window nudges about the
 * wrong months. Capped at the 36 most recent MONTHS (not rows) — a month with
 * two subjects contributes two rows and must not eat two of the 36 slots.
 */
export interface ProgressTimelineReviewMonth {
  month: MonthKey;
  subjectId: string;
  subjectName: string;
  hasReview: boolean;
}

/** GET /api/students/:id/progress-timeline */
export interface ProgressTimeline {
  studentId: string;
  studentName: string;
  nickname: string | null;
  /** Newest month first. */
  months: ProgressTimelineMonth[];
  /** Every taught (month, subject), newest month first, independent of
   *  `?months=`. Always present; `[]` when the student has never been
   *  marked present/absent/excused for anything. */
  reviewMonths: ProgressTimelineReviewMonth[];
}

// ── Monthly reviews ──────────────────────────────────────────────────────

/** THI-348. DRAFT = written but not approved: invisible to parents and never
 *  counted as "đã viết". Everything a parent can read is PUBLISHED. */
export type MonthlyReviewStatus = 'DRAFT' | 'PUBLISHED';

export interface MonthlyReviewDto {
  id: string;
  studentId: string;
  subjectId: string;
  subjectName: string;
  month: MonthKey;
  content: string;
  aiDrafted: boolean;
  status: MonthlyReviewStatus;
  teacherName: string | null;
  updatedAt: string;
}

export interface MonthlyReviewRosterRow {
  studentId: string;
  studentName: string;
  nickname: string | null;
  /** A PUBLISHED review exists (the parent can read it). A draft is not a
   *  review here: see `reviewStatus`. */
  hasReview: boolean;
  /** THI-348: `null` = nothing written, DRAFT = waiting for approval. */
  reviewStatus: MonthlyReviewStatus | null;
  aiDrafted: boolean;
  reviewUpdatedAt: string | null;
  reviewTeacherName: string | null;
  /** Session notes of BOTH tiers this student can draw on this month — a
   *  personal note always counts; a class note counts only for a session
   *  this student attended (PRESENT/EXCUSED). Same rule `collectDraftNotes`
   *  uses to build the AI draft, so this gates exactly the button it
   *  drives (THI-105 fix wave F2). */
  noteCount: number;
  sessions: number;
  present: number;
  excused: number;
  absent: number;
}

/** One recurring slot of the subject, for the sweep page's breadcrumb
 *  ("Piano · Th 3, Th 5 · 17:00", D2). `dayOfWeek` is Prisma's 1=Mon..7=Sun,
 *  `startTime` is "HH:mm" — both copied straight off `ClassSlot`, so the VN
 *  weekday word is chosen by the dashboard's i18n, never by the API. */
export interface MonthlyReviewSweepSlot {
  dayOfWeek: number;
  startTime: string;
}

/** GET /api/subjects/:id/monthly-reviews?month=YYYY-MM (THI-189: replaces the
 *  cycle-1 slot-scoped sweep — a monthly review is per SUBJECT, and naming a
 *  slot on this payload invited exactly the "Vẽ T5" nudge the dev smoke
 *  flagged; a student in two slots of the subject now surfaces once, not
 *  once per slot). No `slotId`/`slotName` — nothing here is slot-scoped any
 *  more, so nothing should look slot-shaped. */
export interface MonthlyReviewSweep {
  subjectId: string;
  subjectName: string;
  month: MonthKey;
  rows: MonthlyReviewRosterRow[];
  /** ACTIVE slots of this subject, ordered by dayOfWeek then startTime. */
  slots: MonthlyReviewSweepSlot[];
}

/** One row of GET /api/monthly-reviews/summary?month=YYYY-MM — one per
 *  subject that has an active roster this month (the SAME roster rule the
 *  sweep uses, so the index page and the sweep can never disagree; see the
 *  route's own comment for why "roster" here is membership-window-based, not
 *  literally "had a session"). Drives both the "Nhận xét tháng" index page
 *  and the workbench nudge from one call. */
export interface MonthlyReviewSummaryRow {
  subjectId: string;
  subjectName: string;
  total: number;
  /** PUBLISHED reviews only. */
  written: number;
  /** THI-348: drafts waiting for approval (not part of `written`). */
  drafts: number;
  /** Roster students with NO student-tier session note carrying content in
   *  this subject this month (THI-193, CD review item 3). The index page's
   *  "N học viên chưa có ghi chú buổi" sub-line; 0 hides it. */
  noNoteCount: number;
}

/** POST …/ai-draft — a draft, NOT a saved review. */
export interface MonthlyReviewDraft {
  content: string;
  sourceNoteCount: number;
}

// ── Progress link ────────────────────────────────────────────────────────

export interface ProgressLink {
  url: string;
}

/** GET /api/students/progress-links/summary — counts over the org's ACTIVE
 *  students, for the bulk-create confirm dialog's "Sẽ tạo link cho {{n}} em
 *  chưa có, {{m}} em đã có giữ nguyên." */
export interface ProgressLinkSummary {
  total: number;
  withLink: number;
  withoutLink: number;
}

/** POST /api/students/progress-links/bulk. Never rotates — `created` counts
 *  students who had no token and now do; `existing` counts students who
 *  already had one and were left untouched. */
export interface BulkProgressLinkResult {
  created: number;
  existing: number;
}

// ── The public parent page ───────────────────────────────────────────────

export interface PublicProgressDay {
  /** 'YYYY-MM-DD' of the session's UTC calendar day. */
  date: string;
  sessionId: string;
  status: AttendanceStatus;
}

export interface PublicProgressSession {
  id: string;
  date: string;
  /** 'HH:mm' and 'HH:mm', both read with UTC getters off the wall clock. */
  startTime: string;
  endTime: string;
  subjectName: string;
  status: AttendanceStatus;
  /** Returned for EVERY session in the month, including ones the child
   *  missed: a parent whose child was absent still benefits from knowing what
   *  the class did (spec §2 / handoff §7.6). */
  classNote: string | null;
  myNote: string | null;
  /** THI-346: the teacher's level for this session, null when unrated or
   *  when the subject's switch is off. Rendered as its own line under the
   *  session header. */
  myRating: SessionRating | null;
  /** Class homework then personal homework, blanks dropped — the merged list
   *  the page renders as a `<ul>`. Sectioned homework is flattened into it as
   *  "Tên mục: nội dung" so a page that ignores the fields below still shows
   *  everything. */
  homework: string[];
  /** THI-193 sectioned content. Empty for free-text notes. */
  classItems: NoteItem[];
  myItems: NoteItem[];
  /** Homework merged by template item: the class value and the child's own
   *  value under one name (handoff 19 §6). `name === null` never occurs here;
   *  free-text homework stays in `homework` only. */
  homeworkItems: { itemId: string; name: string; common: string | null; personal: string | null }[];
  /** THI-196: class photos first, then the child's own (handoff 21 §3). */
  homeworkPhotos: PublicHomeworkPhoto[];
}

export interface PublicProgressReview {
  subjectName: string;
  teacherName: string | null;
  month: MonthKey;
  content: string;
}

export interface PublicProgressOpen {
  closed: false;
  /** `progressDisplayName(name, nickname)` — the tên gọi. Still carried
   *  because the per-session labels read "Nhận xét riêng cho {tên gọi}". */
  studentDisplayName: string;
  /** The student's full name. THI-190 §11.5 REVERSED cycle 1's tên-gọi-only
   *  rule: the nickname is optional, so a student without one rendered a
   *  header built from the last word of their name, which read as odd or
   *  blank. The page now shows "Họ tên (tên ở nhà)", or the full name alone.
   *  The link is unguessable, rotatable, `noindex` and `Disallow`ed in
   *  robots.txt; a parent already knows their own child's name. */
  studentName: string;
  /** The tên ở nhà the family actually uses, trimmed, or `null` when the
   *  student has none. Fix round 1 (THI-190 §11.5 review): the band must NOT
   *  use `progressDisplayName`'s last-word GUESS to decide whether to show a
   *  parenthetical — that guess is fine for in-body sentences ("Nhận xét
   *  riêng cho Ân") but wrong for the header rule, which needs to know a
   *  REAL nickname exists, not merely that the tên gọi differs from the
   *  full name string (a multi-word name with no nickname always differs
   *  from its own last word). */
  nickname: string | null;
  orgName: string;
  /** THI-263: the centre's logo (public CDN URL) for the band and the installed app's icon; null = LopHoc icon. */
  orgLogoUrl: string | null;
  /** THI-263: `#rrggbb` or null for the default green (see `branding.ts`). */
  brandColor: string | null;
  /** Drives the "Tạo bằng LopHoc.app" attribution, same rule as the invoice. */
  showsWatermark: boolean;
  subjects: string[];
  month: MonthKey;
  /** THI-348: true when `month` is the one the page opens on with no
   *  `?month`: the newest month up to now that has a session (usually the
   *  calendar month; an older one for a paused student). The page collapses
   *  the review card there and opens it in full on a month paged back to. */
  isDefaultMonth: boolean;
  /** Months that have at least one session, newest first — the ‹ › bounds. */
  months: MonthKey[];
  stats: { present: number; absent: number; excused: number };
  /** THI-346: how many of the month's sessions got each level, best first,
   *  zero counts omitted; empty when nothing was rated. A distribution, not
   *  an average: averaging emoji is meaningless and hides a bad week. */
  ratings: { level: SessionRating; count: number }[];
  days: PublicProgressDay[];
  sessions: PublicProgressSession[];
  /** PUBLISHED reviews of the month being viewed. */
  monthlyReviews: PublicProgressReview[];
  /** THI-348: the immediately preceding month's PUBLISHED reviews, filled
   *  ONLY when the month being viewed is the one the page opens on by
   *  default and it has no review of its own; `[]` otherwise. Teachers write
   *  month N's review on day 1-2 of N+1, when the page already opens on N+1. */
  previousReviews: PublicProgressReview[];
}

/**
 * The org's plan no longer allows the link (or the org degraded). A saved
 * parent link must say "tạm đóng", never "not found" — and it carries the
 * names so the parent can see they opened the right link (handoff §7.1).
 */
export interface PublicProgressClosed {
  closed: true;
  studentDisplayName: string;
  /** Same reversal as the open branch (THI-190 §11.5) — the soft-closed band
   *  must name the child so a parent knows they opened the right link. */
  studentName: string;
  /** Same rule as the open branch's `nickname` (fix round 1) — trimmed, or
   *  `null` when the student has none. */
  nickname: string | null;
  orgName: string;
  /** THI-263: the centre's logo (public CDN URL) for the band and the installed app's icon; null = LopHoc icon. */
  orgLogoUrl: string | null;
  /** THI-263: `#rrggbb` or null for the default green (see `branding.ts`). */
  brandColor: string | null;
}

export type PublicProgress = PublicProgressOpen | PublicProgressClosed;

// ── The invoice's review section ─────────────────────────────────────────

export interface PublicInvoiceLearningReview {
  studentName: string;
  studentDisplayName: string;
  subjectName: string;
  /** Resolved from the review's `updatedById`. Multi-teacher centres: the
   *  parent wants to know who wrote it (handoff §7.5). */
  teacherName: string | null;
  month: MonthKey;
  content: string;
  /** Present only when that student HAS a token AND the org's plan allows the
   *  link — it drives the closing sentence, which vanishes otherwise. */
  progressUrl: string | null;
}

/** One (student, subject) pair behind a chốt-sổ invoice's billed periods, for
 *  the TEACHER-side invoice detail card (spec §10.4a) — same derivation as
 *  `PublicInvoiceLearningReview` above (bù-trừ month, live-fetched, never
 *  snapshotted), but shaped for a checklist rather than prose: whether the
 *  review exists yet and whether the student has a parent link, not the
 *  review's own content. */
export interface InvoiceReviewTarget {
  studentId: string;
  studentName: string;
  subjectId: string;
  subjectName: string;
  month: MonthKey;
  hasReview: boolean;
  /** `student.progressToken !== null` — plan-gating is a FRONTEND decision
   *  (whether to show the bulk-create prompt at all), not encoded here. */
  hasProgressLink: boolean;
}

/** THI-263: what the per-link manifest and icon routes need, nothing more. */
export interface PublicProgressApp {
  /** "Sổ liên lạc <tên> · <trung tâm>" */
  name: string;
  /** Nickname, else the last two words of the full name. */
  shortName: string;
  orgName: string;
  orgLogoUrl: string | null;
  brandColor: string | null;
}
